import styled, { css } from 'styled-components';
import tinycolor from 'tinycolor2';
import NumberFormat from 'react-number-format';

import { Button, ModalContainer } from 'components/core';
import { Typography } from 'src/typography';

const brandColor = css`
  ${({ theme }) => theme.customized.colors.buttonsLinks}
`;

const contentLayout = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
`;

export const StyledModalContainer = styled(ModalContainer)`
  ${contentLayout}
`;

export const DrawerContent = styled.div`
  ${contentLayout}
`;

export const ModalTitle = styled(Typography.Title)`
  color: var(--dcm-add-funds-modal-title-color, ${({ theme }) => theme.colors.primaryBlack});
  margin: 0;
`;

export const SectionLabel = styled(Typography.Label)`
  color: var(--dcm-add-funds-modal-section-label-color, ${({ theme }) => theme.colors.primaryBlack});
  letter-spacing: 1px;
`;

export const BankRow = styled.div`
  align-items: center;
  border: 1px solid var(--dcm-add-funds-modal-bank-row-border, ${({ theme }) => theme.colors.grey[85]});
  border-radius: var(--dcm-add-funds-modal-bank-row-border-radius, 12px);
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 16px;
  width: 100%;
`;

export const BankInfoGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
`;

export const BankLogoTile = styled.div`
  align-items: center;
  border: 1px solid var(--dcm-add-funds-modal-bank-logo-border, ${({ theme }) => theme.colors.grey[85]});
  border-radius: var(--dcm-add-funds-modal-bank-logo-border-radius, 8px);
  display: flex;
  flex-shrink: 0;
  height: 44px;
  justify-content: center;
  overflow: hidden;
  width: 44px;
`;

export const BankLogo = styled.img`
  height: 100%;
  object-fit: contain;
  width: 100%;
`;

export const BankTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const BankName = styled(Typography.Body)`
  color: var(--dcm-add-funds-modal-bank-name-color, ${({ theme }) => theme.colors.primaryBlack});
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const BankAccountDetail = styled(Typography.Body)`
  color: var(--dcm-add-funds-modal-bank-detail-color, ${({ theme }) => theme.colors.grey[45]});
`;

export const AmountPresetRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

export const AmountPresetButton = styled(Button)<{ isSelected: boolean }>`
  background: var(
    --dcm-add-funds-modal-preset-bg,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90)}
  );
  color: var(--dcm-add-funds-modal-preset-color, ${brandColor});
  flex: 1;

  ${({ isSelected }) =>
    isSelected &&
    css`
      background: var(--dcm-add-funds-modal-preset-selected-bg, ${brandColor});
      color: var(--dcm-add-funds-modal-preset-selected-color, ${({ theme }) => theme.colors.white});
    `}

  &:disabled {
    opacity: 0.55;
  }
`;

export const AmountDisplay = styled.div<{ hasError?: boolean }>`
  align-items: center;
  background: var(--dcm-add-funds-modal-amount-display-bg, ${({ theme }) => theme.colors.grey[95]});
  border: 1px solid transparent;
  border-radius: var(--dcm-add-funds-modal-amount-display-border-radius, 12px);
  display: flex;
  justify-content: center;
  padding: 28px 16px;
  width: 100%;

  ${({ hasError }) =>
    hasError &&
    css`
      border-color: var(--dcm-add-funds-modal-error-color, ${({ theme }) => theme.colors.red[55]});
    `}
`;

export const AmountValue = styled(Typography.Heading)`
  color: var(--dcm-add-funds-modal-amount-color, ${({ theme }) => theme.colors.primaryBlack});
  margin: 0;
`;

export const AmountCurrencySymbol = styled(AmountValue)`
  color: var(--dcm-add-funds-modal-currency-symbol-color, ${({ theme }) => theme.colors.grey[45]});
  margin-right: 8px;
`;

export const CustomAmountInput = styled(NumberFormat)`
  background: transparent;
  border: 0;
  color: var(--dcm-add-funds-modal-amount-color, ${({ theme }) => theme.colors.primaryBlack});
  font-family: inherit;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  min-width: 1ch;
  padding: 0;
  text-align: left;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 40px;
  }

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--dcm-add-funds-modal-currency-symbol-color, ${({ theme }) => theme.colors.grey[45]});
  }
`;

export const AmountErrorText = styled(Typography.Body)`
  color: var(--dcm-add-funds-modal-error-color, ${({ theme }) => theme.colors.red[55]});
  margin: 0;
`;

export const SubmittingHelpText = styled(Typography.Body)`
  color: var(--dcm-add-funds-modal-submitting-help-color, ${({ theme }) => theme.colors.grey[45]});
  margin: 0;
  text-align: center;
`;

export const CtaLoaderWrap = styled.span`
  display: inline-flex;
  margin-right: 10px;
  vertical-align: middle;
`;
